import { useState, useMemo } from 'react'
import { Check, ChevronsUpDown, Clock, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { formatInTimeZone } from 'date-fns-tz'

// Helper function to get GMT offset for a timezone
function getGMTOffset(timezone: string): string {
  try {
    const now = new Date()
    const offset = formatInTimeZone(now, timezone, 'xxx') // Returns format like +05:30 or -08:00
    const hours = parseInt(offset.slice(0, 3), 10)
    const minutes = offset.slice(4, 6)

    if (hours === 0 && minutes === '00') {
      return 'GMT'
    }

    const sign = hours >= 0 ? '+' : ''
    if (minutes === '00') {
      return `GMT${sign}${hours}`
    }
    return `GMT${sign}${hours}:${minutes}`
  } catch {
    return ''
  }
}

// Common timezones grouped by region
const TIMEZONES = [
  // UTC
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)', region: 'UTC' },
  
  // Americas
  { value: 'America/New_York', label: 'Eastern Time (US & Canada)', region: 'Americas' },
  { value: 'America/Chicago', label: 'Central Time (US & Canada)', region: 'Americas' },
  { value: 'America/Denver', label: 'Mountain Time (US & Canada)', region: 'Americas' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)', region: 'Americas' },
  { value: 'America/Anchorage', label: 'Alaska', region: 'Americas' },
  { value: 'Pacific/Honolulu', label: 'Hawaii', region: 'Americas' },
  { value: 'America/Toronto', label: 'Toronto', region: 'Americas' },
  { value: 'America/Mexico_City', label: 'Mexico City', region: 'Americas' },
  { value: 'America/Sao_Paulo', label: 'São Paulo', region: 'Americas' },
  { value: 'America/Buenos_Aires', label: 'Buenos Aires', region: 'Americas' },
  
  // Europe
  { value: 'Europe/London', label: 'London', region: 'Europe' },
  { value: 'Europe/Paris', label: 'Paris', region: 'Europe' },
  { value: 'Europe/Berlin', label: 'Berlin', region: 'Europe' },
  { value: 'Europe/Rome', label: 'Rome', region: 'Europe' },
  { value: 'Europe/Madrid', label: 'Madrid', region: 'Europe' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam', region: 'Europe' },
  { value: 'Europe/Brussels', label: 'Brussels', region: 'Europe' },
  { value: 'Europe/Vienna', label: 'Vienna', region: 'Europe' },
  { value: 'Europe/Warsaw', label: 'Warsaw', region: 'Europe' },
  { value: 'Europe/Moscow', label: 'Moscow', region: 'Europe' },
  
  // Asia
  { value: 'Asia/Dubai', label: 'Dubai', region: 'Asia' },
  { value: 'Asia/Kolkata', label: 'India Standard Time (IST)', region: 'Asia' },
  { value: 'Asia/Bangkok', label: 'Bangkok', region: 'Asia' },
  { value: 'Asia/Singapore', label: 'Singapore', region: 'Asia' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong', region: 'Asia' },
  { value: 'Asia/Shanghai', label: 'Beijing/Shanghai', region: 'Asia' },
  { value: 'Asia/Tokyo', label: 'Tokyo', region: 'Asia' },
  { value: 'Asia/Seoul', label: 'Seoul', region: 'Asia' },
  
  // Australia & Pacific
  { value: 'Australia/Sydney', label: 'Sydney', region: 'Australia & Pacific' },
  { value: 'Australia/Melbourne', label: 'Melbourne', region: 'Australia & Pacific' },
  { value: 'Australia/Brisbane', label: 'Brisbane', region: 'Australia & Pacific' },
  { value: 'Australia/Perth', label: 'Perth', region: 'Australia & Pacific' },
  { value: 'Pacific/Auckland', label: 'Auckland', region: 'Australia & Pacific' },
  
  // Africa
  { value: 'Africa/Cairo', label: 'Cairo', region: 'Africa' },
  { value: 'Africa/Johannesburg', label: 'Johannesburg', region: 'Africa' },
  { value: 'Africa/Lagos', label: 'Lagos', region: 'Africa' },
  { value: 'Africa/Nairobi', label: 'Nairobi', region: 'Africa' },
]

interface TimezoneSelectorProps {
  value: string
  onChange: (timezone: string) => void
  disabled?: boolean
}

export function TimezoneSelector({ value, onChange, disabled }: TimezoneSelectorProps) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const selectedTimezone = TIMEZONES.find((tz) => tz.value === value)
  const currentTime = formatInTimeZone(new Date(), value, 'PPpp')

  // Get GMT offset for selected timezone
  const selectedGMTOffset = useMemo(() => getGMTOffset(value), [value])

  // Filter timezones based on search query
  const filteredTimezones = useMemo(() => {
    if (!searchQuery.trim()) return TIMEZONES
    const query = searchQuery.toLowerCase()
    return TIMEZONES.filter(
      (tz) =>
        tz.value.toLowerCase().includes(query) ||
        tz.label.toLowerCase().includes(query) ||
        tz.region.toLowerCase().includes(query)
    )
  }, [searchQuery])

  // Group filtered timezones by region
  const groupedTimezones = useMemo(() => {
    return filteredTimezones.reduce((acc, tz) => {
      if (!acc[tz.region]) {
        acc[tz.region] = []
      }
      acc[tz.region].push(tz)
      return acc
    }, {} as Record<string, typeof TIMEZONES>)
  }, [filteredTimezones])

  const handleSelect = (timezoneValue: string) => {
    onChange(timezoneValue)
    setOpen(false)
    setSearchQuery('')
  }

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={(isOpen) => {
        setOpen(isOpen)
        if (!isOpen) setSearchQuery('')
      }}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
            disabled={disabled}
          >
            <span className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              {selectedTimezone ? (
                <>
                  {selectedTimezone.label}
                  <span className="text-xs text-muted-foreground">({selectedGMTOffset})</span>
                </>
              ) : (
                'Select timezone...'
              )}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0">
          {/* Search input */}
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <Input
              placeholder="Search timezone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-11 border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </div>

          {/* Scrollable list */}
          <div
            className="max-h-[300px] overflow-y-auto"
            style={{
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              touchAction: 'pan-y',
            }}
            onWheel={(e) => e.stopPropagation()}
          >
            {filteredTimezones.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                No timezone found.
              </p>
            ) : (
              Object.entries(groupedTimezones).map(([region, timezones]) => (
                <div key={region} className="p-1">
                  <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                    {region}
                  </div>
                  {timezones.map((timezone) => (
                    <div
                      key={timezone.value}
                      onClick={() => handleSelect(timezone.value)}
                      className={cn(
                        'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none',
                        'hover:bg-accent hover:text-accent-foreground',
                        value === timezone.value && 'bg-accent'
                      )}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          value === timezone.value ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <span className="flex-1">{timezone.label}</span>
                      <span className="text-xs text-muted-foreground ml-2">
                        {getGMTOffset(timezone.value)}
                      </span>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Current time preview */}
      <p className="text-sm text-muted-foreground">
        Current time: {currentTime}
      </p>
    </div>
  )
}

